import type {
  ColumnOrderState,
  ColumnSizingState,
  ColumnVisibilityState,
  RowSelectionState,
} from "@tanstack/react-table";
import { useCallback, useState } from "react";

import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";

// Retained so the one-time settings seed picks up geo-hidden visibility from the legacy localStorage key. Once the
// per-membership seed has shipped for a release and legacy keys are removed, this migration is dead and can be deleted.
function migrateHideGeoColumns(workspaceId: string): void {
  if (!workspaceId) return;
  try {
    const migrationKey = `${workspaceId}-driverGridColumnsMigration`;
    if (localStorage.getItem(migrationKey) === "1") return;

    const storageKey = `${workspaceId}-driverGridColumns`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      parsed.country = false;
      parsed.stateProvince = false;
      localStorage.setItem(storageKey, JSON.stringify(parsed));
    }
    localStorage.setItem(migrationKey, "1");
  } catch {
    // localStorage may be unavailable, at quota, or hold corrupted data —
    // useLocalStorage will fall back to the default visibility state.
  }
}

export function useDriverGridState(workspaceId: string) {
  migrateHideGeoColumns(workspaceId);

  const { settings, updateWebSettings, resetDriverGrid } = useWebSettings();
  const columnVisibility = settings.driverGrid.columnVisibility as ColumnVisibilityState;
  // The pinned ("frozen") columns are positioned by TanStack's native column-pinning state, which is independent of
  // `columnOrder` — so we persist the full order verbatim. No pinned-ID stripping or re-prepend, which is what made the
  // order fail to persist when the async leading-tag column shifted the pinned set between write and read.
  const columnOrder = settings.driverGrid.columnOrder as ColumnOrderState;
  const columnSizing = settings.driverGrid.columnSizing as ColumnSizingState;
  // Absent = use the table's tag-based default (meta.frozen); present = user override. DataTable applies it.
  const columnPinning = settings.driverGrid.columnPinning;

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const onColumnVisibilityChange = useCallback(
    (updater: ColumnVisibilityState | ((prev: ColumnVisibilityState) => ColumnVisibilityState)) => {
      const next = typeof updater === "function" ? updater(columnVisibility) : updater;
      updateWebSettings({ driverGrid: { columnVisibility: next } });
    },
    [columnVisibility, updateWebSettings],
  );

  const onColumnOrderChange = useCallback(
    (updater: ColumnOrderState | ((prev: ColumnOrderState) => ColumnOrderState)) => {
      const next = typeof updater === "function" ? updater(columnOrder) : updater;
      updateWebSettings({ driverGrid: { columnOrder: next } });
    },
    [columnOrder, updateWebSettings],
  );

  const onColumnSizingChange = useCallback(
    (updater: ColumnSizingState | ((prev: ColumnSizingState) => ColumnSizingState)) => {
      const next = typeof updater === "function" ? updater(columnSizing) : updater;
      updateWebSettings({ driverGrid: { columnSizing: next } });
    },
    [columnSizing, updateWebSettings],
  );

  const onColumnPinningChange = useCallback(
    (left: string[]) => {
      updateWebSettings({ driverGrid: { columnPinning: { left } } });
    },
    [updateWebSettings],
  );

  const onRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      setRowSelection((prev) => (typeof updater === "function" ? updater(prev) : updater));
    },
    [],
  );

  return {
    columnVisibility,
    onColumnVisibilityChange,
    columnOrder,
    onColumnOrderChange,
    columnSizing,
    onColumnSizingChange,
    columnPinning,
    onColumnPinningChange,
    rowSelection,
    onRowSelectionChange,
    resetColumns: resetDriverGrid,
  };
}
